/*
 * Relix: "Select Your Size" for apparel in Rebuy widgets
 * Covers Smart Cart, PDP widgets, and cart page widgets.
 * (Post-purchase / thank you runs in Shopify's checkout sandbox and is NOT covered.)
 *
 * How it works:
 * 1. Finds every select.rebuy-select whose options look like sizes (S, M, L, XL, 2XL, "White / S", etc).
 * 2. Injects a disabled "Select Your Size" option at the top and selects it.
 * 3. Blocks the Add click (capture phase, before Rebuy's own handler) until the shopper
 *    actually picks a size, then shows an inline error.
 * 4. Hides Rebuy's default variant subtitle ("White / S") until a size is chosen.
 * 5. Re-applies after Rebuy/Vue re-renders (Smart Cart opening, cart updates, widget refresh).
 */
(function () {
  'use strict';

  var PLACEHOLDER_TEXT = 'Select Your Size';
  var ERROR_TEXT = 'Please select your size';
  var SIZE_RE = /^(xxs|xs|s|m|l|xl|xxl|xxxl|[2-6]xl|[2-6]x|small|medium|large|x-large|xx-large|y(xs|s|m|l|xl)|youth (xs|s|m|l|xl)|\d{1,2}t)$/i;

  // Selects where the shopper has made a real choice. Keyed by element so a fresh
  // render (new product in the slot) starts over with the placeholder.
  var chosen = new WeakSet();

  /* ---------- styles ---------- */
  var css =
    '.rebuy-select.rx-size-error{border-color:#d0021b!important;box-shadow:0 0 0 1px #d0021b;animation:rx-shake .3s}' +
    '[data-rx-hide]{visibility:hidden!important}' +
    '.rx-size-error-msg{color:#d0021b;font-size:13px;line-height:1.3;margin-top:6px}' +
    '@keyframes rx-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}';
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  /* ---------- helpers ---------- */
  function isSizeLabel(text) {
    return text.split('/').some(function (part) {
      return SIZE_RE.test(part.trim());
    });
  }

  function realOptions(select) {
    return Array.prototype.filter.call(select.options, function (o) {
      return !o.hasAttribute('data-rx-placeholder');
    });
  }

  function isSizedSelect(select) {
    var opts = realOptions(select);
    if (opts.length < 2) return false;
    var sized = opts.filter(function (o) { return isSizeLabel(o.textContent); });
    return sized.length >= 2;
  }

  function prepare(select) {
    var opts = realOptions(select);
    if (opts.length === 0) return; // not rendered yet, check again later

    if (!select.dataset.rxSized) {
      select.dataset.rxSized = isSizedSelect(select) ? 'yes' : 'no';
    }
    if (select.dataset.rxSized !== 'yes') return;
    if (chosen.has(select)) return;

    var ph = select.querySelector('option[data-rx-placeholder]');
    if (!ph) {
      ph = document.createElement('option');
      ph.value = '';
      ph.textContent = PLACEHOLDER_TEXT;
      ph.disabled = true;
      ph.setAttribute('data-rx-placeholder', '');
      ph.className = 'rebuy-product-options__option';
      select.insertBefore(ph, select.firstChild);
    }
    // Vue resets the visible selection on re-render; put the placeholder back.
    if (select.value !== '') ph.selected = true;
    setSubtitleHidden(select, true);
  }

  // The product card that owns this select: highest ancestor holding only this one select.
  function cardFor(select) {
    var el = select.parentElement, card = null;
    for (var depth = 0; el && depth < 8; depth++, el = el.parentElement) {
      if (el.querySelectorAll('select.rebuy-select').length > 1) break;
      card = el;
    }
    return card;
  }

  // Rebuy prints the default variant ("White / S") under the title. Hide it until a size is picked.
  function setSubtitleHidden(select, hidden) {
    var card = cardFor(select);
    if (!card) return;
    var titles = card.querySelectorAll('.rebuy-variant-title');
    for (var i = 0; i < titles.length; i++) {
      if (hidden) titles[i].setAttribute('data-rx-hide', '');
      else titles[i].removeAttribute('data-rx-hide');
    }
  }

  function scan() {
    var selects = document.querySelectorAll('select.rebuy-select');
    for (var i = 0; i < selects.length; i++) prepare(selects[i]);
  }

  // Walk up from the Add button until we find the one product's dropdown.
  function findSelectFor(button) {
    var el = button.parentElement;
    for (var depth = 0; el && depth < 8; depth++, el = el.parentElement) {
      var found = el.querySelectorAll('select.rebuy-select');
      if (found.length === 1) return found[0];
      if (found.length > 1) return null; // climbed into the whole widget, stop
    }
    return null;
  }

  function showError(select) {
    select.classList.remove('rx-size-error');
    void select.offsetWidth; // restart shake animation
    select.classList.add('rx-size-error');
    var next = select.nextElementSibling;
    if (!next || !next.hasAttribute('data-rx-error')) {
      var msg = document.createElement('div');
      msg.className = 'rx-size-error-msg';
      msg.setAttribute('data-rx-error', '');
      msg.setAttribute('role', 'alert');
      msg.textContent = ERROR_TEXT;
      select.insertAdjacentElement('afterend', msg);
    }
  }

  function clearError(select) {
    select.classList.remove('rx-size-error');
    var next = select.nextElementSibling;
    if (next && next.hasAttribute('data-rx-error')) next.remove();
  }

  /* ---------- events ---------- */

  // Shopper picks a real size: remember it and clear any error.
  document.addEventListener('change', function (e) {
    var select = e.target;
    if (!select.matches || !select.matches('select.rebuy-select')) return;
    if (select.dataset.rxSized !== 'yes') return;
    if (select.value !== '') {
      chosen.add(select);
      clearError(select);
      setSubtitleHidden(select, false);
    }
  }, true);

  // Block Add until a size is chosen. Capture phase runs before Rebuy's handler.
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('button');
    if (!button) return;
    var looksLikeAdd = button.classList.contains('rebuy-button') || /add/i.test(button.textContent);
    if (!looksLikeAdd) return;

    var select = findSelectFor(button);
    if (!select || select.dataset.rxSized !== 'yes') return;
    if (chosen.has(select)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    showError(select);
    select.focus();
  }, true);

  /* ---------- keep up with Rebuy re-renders ---------- */
  var queued = false;
  function queueScan() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(); });
  }

  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });

  ['rebuy.ready', 'rebuy.view', 'rebuy.add', 'rebuy:smartcart.ready', 'rebuy:smartcart.show', 'rebuy:cart.change']
    .forEach(function (name) { document.addEventListener(name, queueScan); });

  // Safety net: Vue can reset the selection without a DOM mutation.
  setInterval(scan, 1000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
